import { CanvasPage } from "./interface-studio-canvas";

export interface EventLocation {
  tba: boolean;
  fullAddress: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  latitude: string | null;
  longitude: string | null;
  placeId: string | null;
}

export interface CalendarEvent {
  // core fields from your payload
  title: string;
  description: string;
  status: number;
  eventLocation: EventLocation | null;
  scheduledDate: string;
  scheduledEndDate: string;

  // common optional fields that sometimes appear in event objects
  id?: string;
  createdBy?: string;
  presenters?: string[]; // e.g. list of presenter names or ids
  durationMinutes?: number;
  timezone?: string;
  createdAt?: string;
  updatedAt?: string;

  // allow any other fields without breaking type checking
  [key: string]: unknown;
}

export interface GetCalendarServiceParams {
  startDate: string;
  endDate: string;
}

export interface GetCalendarServiceResponse {
  hosting: CalendarEvent[];
  presenting: CalendarEvent[];
  attending: CalendarEvent[];
}

export enum SortDirection {
  ASCENDING = 0,
  DESCENDING = 1,
}

export interface GetEventsQueryParams {
  query?: string; // Query
  startDate?: string; // StartDate
  endDate?: string; // EndDate
  page?: number; // Page
  pageLimit?: number; // PageLimit
  range?: string; // Range
  sortBy?: string; // SortBy
  sortDirection?: SortDirection; // 0 - ascending; 1 - descending

  teamId?: string; // teamId=...
  organizerId?: string; // organizerId
  speakerId?: string; // speakerId
  timelineStatus?: string; // timelineStatus
  eventScope?: string; // eventScope
  extendTicket?: boolean; // extendTicket
  agendaType?: string;
  extendSpeakerSummary?: boolean; //extend speaker summary
}

export interface Pagination {
  totalData: number;
  pageLimit: number;
  currentPage: number;
  currentPageData: number;
  totalPages: number;
  prevPage: number | null;
  nextPage: number | null;
}

export interface EventItem {
  id: string;
  friendlyId: string;
  title: string;
  description: string | null;
  category: string | null;

  startDate: string;
  endDate: string;
  timeStarted: string | null;
  timeEnded: string | null;

  dateModified: string;
  dateCreated: string;

  teamId: string;
  published: boolean;
  agenda: any[]; // If you want, I can strongly type this too.

  role: string | null;
  currentSession: string | null;
  currentPresentation: string | null;

  status: number;
  bannerPosition: number;
  bannerStyle: number;
  projectorControl: boolean;
  displayInstruction: number;
  authMode: number;

  summary: string | null;
  summarySent: boolean;
  authorizedRequest: boolean;

  timezone: string;
  checkedInVote: boolean;
  enableSummary: boolean;
  canDownload: number;
  questionsAllowed: boolean;
  hasJudges: boolean;
  isPublic: boolean;

  eventLink: string | any[] | null; // API is inconsistent (null / array)
  eventLocation: EventLocation | null;

  minimumTicket: number;
  maximumTicket: number;
  currency: string | null;

  organizerId: string | null;
  currentDay: string | null;
  bannerUrl: string | null;

  allowPostEventAccess: boolean;

  banner: string;

  rating?: number;
  attendees?: number;
  engagementScore?: number;
  ratingCount?: number;
}

export interface EventLocation {
  tba: boolean;
  fullAddress: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  latitude: string | null;
  longitude: string | null;
  placeId: string | null;
}

export interface EventsFilterInterface {
  query: string | null;
  startDate: string | null;
  endDate: string | null;
  page: number;
  pageLimit: number;
  range: number;
  sortBy: string | null;
  sortDirection: number;
}

export interface GetEventsResponse {
  message: string;
  status: string;
  data: {
    pagination: Pagination;
    events: EventItem[];
    filter: EventsFilterInterface;
  };
}

// Create Event
export interface AddGuestsToEventPayload {
  eventId: string;
  emails: string[];
  role: number;
}

export interface UpdateEventSettingsPayload {
  eventId: string;
  teamId: string;
  allowQuestion: boolean;
  enableWaitingRoom: boolean;
  enableSummary: boolean;
  enableConversation: boolean;
  authMode: number;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  checkedInVote: boolean;
  hasJudges: boolean;
  isPublic: boolean;
  canDownload: number;
  allowPostEventAccess: boolean;
}

export interface CreateEventPayload {
  isPublic?: boolean;
  title: string;
  description: string;
  startDate: string;
  teamId: string;
  location?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  eventLink?: { app: string; link: string } | null;
  quickPresentation?: boolean;
  bannerUrl?: string;
}

export interface CreateEventResponse {
  id: string;
  title: string;
  startDate: string;
  [key: string]: any;
}

// Add Session to Event
export interface AddSessionPayload {
  eventId: string;
  teamId: string;
  sessions: Array<{
    title: string;
    duration: string;
    collaboratorId: string;
    position: string;
    agendaType: string;
    description: string;
    start: string;
    debrief: string;
    speakers: string;
  }>;
}

export interface AddSessionResponse {
  sessions: Array<{ id: string; title: string }>;
}

// Publish Event
export interface PublishEventPayload {
  eventId: string;
  teamId: string;
}

export interface PublishEventResponse {
  success: boolean;
  message?: string;
}

// Get Event Details

export enum EventStatus {
  Scheduled,
  Started,
  Ended,
}

export type AgendaType =
  | "Keynote"
  | "Plenary"
  | "Panel Discussion"
  | "Workshop"
  | "Training"
  | "Competition/Pitch"
  | "Fireside Chat"
  | "Breakout/Roundtable"
  | "Networking Session"
  | "Exhibition/Trade Show"
  | "Awards Ceremony/Gala"
  | "Q&A/AMA"
  | "Poster Session"
  | "Product Demo/Launch"
  | "Registration"
  | "Break";

export interface SpeakerInterface {
  id: string;
  category: string;
  categoryId: string;
  speakerId: string;
  avatar: null | string;
  eventId: string;
  email: string;
  name: string;
  description: string;
  facebook: string;
  instagram: string;
  linkedIn: string;
  youtube: string;
  otherPlatforms: null | string;
  avatarIndex?: number;
  speaker?: string;
  lineUpType?: number;
  designation?: string;
  company?: string;
}

export interface CollaboratorsInterface {
  sessionId: string;
  presentationId: string;
  eventId: string;
  eventFriendlyId: null | string;
  email: string;
  role: string;
  status: string;
  userId: string;
  fullName: string;
  id: string;
}

export interface UnlockedInterface {
  [key: string]: number[];
}

export interface PresentationDataInterface {
  spaceId: string | null;
  friendlyId: string;
  title: string;
  thumbnail: string;
  userId: string;
  dateAdded: string;
  expiry: null | string;
  startTime: string;
  endTime: string;
  scheduledTime: string;
  visibility: boolean;
  currentSlide: number;
  allowedDomains: string[];
  passcode: string;
  status: number;
  authMode: number;
  type: number;
  canDownload: number;
  questionsAllowed: boolean;
  deleted: boolean;
  currentItem: string;
  duration: number;
  questions: number;
  visitors: number;
  views: number;
  id: string;
  requestPasscode: boolean;
  waitingRoom: boolean;
  watermark: string;
  unlocked?: UnlockedInterface;
  isEvent: boolean;
  eventId: string;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  itemCount: number;
  dateModified?: string;
  eventCount: number;
  summary: string | null;
}

export interface SlideInterface {
  id: string;
  url: string;
  position: number;
  title: string;
  note: string;
  imgUrl: string;
  unlocked?: boolean;
  type?: string;
  textContent?: string;
  page?: CanvasPage;
  // rowKey?: string; This updated to id for html docs on 25/06/2025
  attributes?: {
    section?: Record<string, string>; // e.g. 'data-transition', 'data-background-gradient', etc.
    video?: Record<string, string>; // e.g. 'data-autoplay', 'src'
    iframe?: Record<string, string>; // e.g. 'src', 'data-allowfullscreen'
    // if you ever need overlays, captions, etc:
    [otherTag: string]: Record<string, string> | undefined;
  };
}

export interface DocDetailsInterface {
  slides: SlideInterface[];
  id: string;
  parentId: null | string;
  folderId: string;
  url: string;
  title: string;
  note: null | string;
  documentId: string;
  dateAdded?: string;
  position: number;
}

export interface AgendaPresentationInterface {
  presentation: PresentationDataInterface;
  doc: DocDetailsInterface[];
}

export interface AgendaInterface {
  eventId: string;
  position: number;
  title: string;
  time: string;
  duration: number;
  presentationId: string;
  collaborators: CollaboratorsInterface[];
  speakers: SpeakerInterface[];
  presentation: AgendaPresentationInterface;
  id: string;
  description: string;
  agendaType: AgendaType;
  start?: string;
  children?: AgendaInterface[];
  avatarUrl?: string | null;
  debrief?: number;
  visibility?: boolean;
  parentId?: string;
  pitchId?: string;
}

export interface DateAgendaInterface {
  date: string;
  title: string;
  sessions: AgendaInterface[];
}

export interface EventLinkInterface {
  app: string;
  link: string;
}

export interface GetEventResponse {
  postEventAccessLinkId?: string | null;
  title: string;
  friendlyId: string | null;
  description: string;
  startDate: string;
  dateCreated: string;
  dateModified: string;
  createdBy: string;
  modifiedBy: string;
  teamId: string;
  published: boolean;
  visibility: boolean;
  waitingRoom: boolean;
  restriction: number;
  domains: null | string[];
  canDownload: number;
  questionsAllowed: boolean;
  watermark: null | string;
  authMode: number;
  id: string;
  status: EventStatus;
  currentSession: null | string;
  agenda: DateAgendaInterface[];
  role: "EventOwner" | "Collaborator" | "Participant";
  banner: string;
  newInvite: boolean;
  currentPresentation: string | null;
  expiry: string | null;
  projectorControl: boolean;
  displayInstruction: number;
  bannerPosition: number;
  bannerStyle: number;
  passcode: string | null;
  category: null | string;
  timeStarted: string;
  timeEnded: string;
  checkedInVote: boolean;
  hasJudges: boolean;
  isPublic: boolean;
  enableSummary: boolean;
  endDate?: string;
  eventLink?: EventLinkInterface[];
  timezone?: string;
  currency?: string;
  minimumTicket?: number;
  maximumTicket?: number;
  longitude?: number;
  latitude?: number;
  organizerId?: string;
  eventLocation?: EventLocation;
  currentDay?: string;
  allowPostEventAccess?: boolean;
  rating?: number;
  attendees?: number;
  engagementScore?: number;
  ratingCount?: number;
}

// export interface GetEventResponse {
//   id: string;
//   title: string;
//   description: string;
//   startDate: string;
//   agenda: Array<{
//     sessions: Array<{
//       id: string;
//       title: string;
//       [key: string]: any;
//     }>;
//     [key: string]: any;
//   }>;
//   [key: string]: any;
// }

// Add Presentation to Event
export interface AddPresentationToEventPayload {
  title: string;
  description: string;
  eventId: string;
  sessionId: string;
  presentationId: string;
}

export interface AddPresentationToEventResponse {
  id: string;
  [key: string]: any;
}

// Add Lineup to Event
export interface AddLineupPayload {
  eventId: string;
  teamId: string;
  formData: FormData;
}

export interface AddLineupResponse {
  speakerId: string;
}

// Get Event Reviews
export interface GetEventReviewsParams {
  eventId?: string;
  presentationId?: string;
  skip?: number;
  pageSize?: number;
  isFeedback?: boolean;
}

export interface ReviewQuestion {
  questionId: string;
  question: string;
  comment: string | null;
  score: number;
}

export interface EventReview {
  id: string;
  presentationId: string;
  eventId: string;
  presentation: string;
  event: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  feedback: string;
  rating: number;
  reviews: ReviewQuestion[];
  date: string;
  accessType: number;
  approved: boolean;
}

export interface EventReviewsSummary {
  overallRating: number;
  judgesRating: number;
  participantRating: number;
  feedbacks: number;
  testimonials: number;
  attendees: number;
  engagementScore: number;
}

export interface GetEventReviewsResponse {
  data: {
    totalCount: number;
    data: EventReview[];
    returned: number;
    skipped: number;
  };
  summary: EventReviewsSummary;
}

export type GetAgendaTypesResponse = AgendaType[];

export interface GetAudioAllowed {
  eventId: string;
  presentationId: string;
  presentaConnectionId: string;
}

export interface AudioAllowed {
  canProcessAudio: boolean;
}

/** A single step inside a playbook flow, as returned by GET /Events/Playbooks. */
export interface EventPlaybookStep {
  stepNumber: number;
  name: string;
  /** e.g. "SendEmail" | "Wait" | "AddContactTag" | "AddEventLabel". */
  type: string;
  summary: string;
  /** Only set on `Wait` steps. */
  waitSeconds: number | null;
  /** Action-specific values supplied by the playbook catalogue. */
  params?: Record<string, string | number | boolean | null>;
}

export interface EventPlaybook {
  code: string;
  title: string;
  description: string;
  triggerEventType: string;
  steps: EventPlaybookStep[];
}

/** The catalogue is keyed by playbook code, e.g. `{ P1_A_ENTRY: {...} }`. */
export type GetEventPlaybooksResponse = Record<string, EventPlaybook>;

export interface EventPlaybookSelection {
  playbookCode: string;
  isActive: boolean;
}

/**
 * Playbooks already picked for an event. The endpoint may return them keyed by
 * code like the catalogue, so consumers should normalise both shapes.
 */
export type GetEventPlaybookSelectionResponse =
  | EventPlaybookSelection[]
  | Record<string, EventPlaybookSelection & Partial<EventPlaybook>>;

export interface UpdateEventPlaybooksPayload {
  eventId: string;
  playbooks: EventPlaybookSelection[];
}

export type UpdateEventPlaybooksResponse = any;
